(ns app.front.view.letter.section
  (:require [app.domain.group.model :as group-model]
            [app.domain.user.authority :as authority]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.file.download :as file-download]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.licence.subs :as licence-subs]
            [app.front.view.workspace.section :as ws-section]
            [app.front.view.workspace.subs :as ws-subs]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(rf/reg-sub ::phase-entity?
  (fn [[_ target-id]]
    (rf/subscribe [::entities/entity target-id]))
  :-> workspace-model/is-phase-entity?)

(rf/reg-sub ::business-entity?
  (fn [[_ target-id]]
    (rf/subscribe [::entities/entity target-id]))
  (fn [target-entity _]
    (eu/is-type? :entity.type/business target-entity)))

(rf/reg-sub ::generate-letter-permitted?
  (fn [[_ target-id]]
    (rf/subscribe [::entities/entity target-id]))
  (fn [target-entity _]
    (cond
      (workspace-model/is-phase-entity? target-entity)
      (<sub [::process/signal-permitted? target-entity [:process.signal/generate-letter]])

      (eu/is-type? :entity.type/licence target-entity)
      (let [process (<sub [::licence-subs/current-licence-process])]
        (process/signal-permitted? process [:process.signal/generate-letter]))

      (eu/is-type? :entity.type/business target-entity)
      (<sub [:session/authority-user?])

      :else
      false)))

(rf/reg-sub ::delete-letter-permitted?
  (fn [[_ target-id]]
    (rf/subscribe [::entities/entity target-id]))
  (fn [target-entity [_ _ letter]]
    (cond
      (workspace-model/is-phase-entity? target-entity)
      (<sub [::process/signal-permitted? target-entity [:process.signal/delete-letter
                                                        {:letter-id (:db/id letter)}]])

      (eu/is-type? :entity.type/licence target-entity)
      (let [process (<sub [::licence-subs/current-licence-process])]
        (process/signal-permitted? process [:process.signal/delete-letter
                                            {:letter-id (:db/id letter)}]))

      (eu/is-type? :entity.type/business target-entity)
      (and (<sub [::ff/enabled? :feature-flag.id/delete-generic-letters])
           (authority/admin-authority-in-group? (<sub [:session/user])
                                                (:domain/authority-group letter)))

      :else
      false)))

(defn resolve-template-type [target-entity]
  (case (:entity/type target-entity)
    :entity.type/licence :letter-pdf-template.type/licence
    :entity.type/business :letter-pdf-template.type/business
    :letter-pdf-template.type/workspace))

(defn get-letter-templates-xform [authority-group-ids template-type]
  (let [where {:entity/type              :entity.type/letter-pdf-template
               :domain/state             :letter-pdf-template.state/active
               :letter-pdf-template/type template-type}]
    (filter (fn [entity]
              (and (contains? authority-group-ids (:domain/authority-group entity))
                   (= (select-keys entity (keys where)) where))))))

(rf/reg-sub ::letter-templates
  (fn [[_ target-id]]
    [(rf/subscribe [::entities/entity target-id])
     (rf/subscribe [:session/authority-groups])])
  (fn [[target-entity user-auth-groups] _]
    (let [auth-groups (if (eu/is-type? :entity.type/business target-entity)
                        user-auth-groups
                        [(group-model/get-authority-group target-entity)])]
      (<sub [::entities/entities (get-letter-templates-xform
                                   (->> auth-groups
                                        (concat (keep group-model/group-superdomain auth-groups))
                                        (map :db/id)
                                        set)
                                   (resolve-template-type target-entity))]))))

(rf/reg-sub ::show-group-name-for-templates?
  :<- [:session/authority-groups]
  (fn [user-auth-groups _]
    (< 1 (count user-auth-groups))))

(rf/reg-event-fx ::generate-letter
  (fn [_ [_ target-id template-id for-business?]]
    (if for-business?
      {:comm/command {:command    :business/generate-letter
                      :data       {:business-id target-id
                                   :template-id template-id}
                      :on-success [::generate-letter-business.success]}}
      {:dispatch [::process/signal target-id [:process.signal/generate-letter
                                              {:template-id template-id}]]})))

(rf/reg-event-fx ::generate-letter-business.success
  (fn [_ [_ status resp]]
    {:dispatch [::entities/apply-changes status resp]}))

(rf/reg-event-fx ::delete-letter
  (fn [_ [_ target-id letter-id for-business?]]
    (if for-business?
      {:comm/command {:command    :business/delete-letter
                      :method     :post
                      :data       {:letter-id letter-id}
                      :on-success [::entities/apply-changes]}}
      {:dispatch [::process/signal target-id [:process.signal/delete-letter
                                              {:letter-id letter-id}]]})))

(rf/reg-sub ::generating?
  (fn [[_ target-id]]
    (rf/subscribe [::letters target-id]))
  (fn [letters _]
    (->> letters
         (some (du/in-state-fn :letter.state/generating))
         boolean)))

(rf/reg-sub ::letters
  (fn [[_ target-id]]
    (rf/subscribe [::entities/entities {:entity/parent target-id
                                        :entity/type   :entity.type/letter}]))
  (fn [letters [_]]
    (->> letters
         (sort-by (fn [letter]
                    [(->> letter (du/in-state? :letter.state/deleted) not)
                     (-> letter :entity/_parent seq nil?)
                     (-> letter :entity/_parent first :entity/created)])))))

(rf/reg-sub ::add-letter-form-open?
  (fn [db _]
    (get db ::add-letter-form-open? false)))

(rf/reg-event-fx ::trigger-add-letter
  (fn [{:keys [db]} _]
    {:db (assoc db ::add-letter-form-open? true)
     :fx [[::futil/scroll-to-section :letters]]}))

(defn template-name-with-group [template]
  (str (:letter-pdf-template/name template)
       " ("
       (-> template :domain/authority-group :group/name)
       ")"))

(defn template-name-fn [show-group-name?]
  (if show-group-name?
    template-name-with-group
    :letter-pdf-template/name))

(defn- create-letter-controls [{:keys [tr target-id templates]}]
  (r/with-let [*template (r/atom nil)]
    [:div.flex.mt-4.gap-4
     (let [show-group-name? (<sub [::show-group-name-for-templates?])
           template-name    (template-name-fn show-group-name?)]
       (if (<sub [::ws-subs/loading-config?])
         [cc/select-skeleton]
         [ui-select/select
          {:id           "letter-selection"
           :allow-empty? false
           :values       (sort-by template-name templates)
           :value        @*template
           :on-change    (partial reset! *template)
           :val->str     template-name}]))
     (if (<sub [::generating? target-id])
       [:div.mt-2.mr-8
        (cc/loading-spinner {:with-overlay false
                             :small        true})]
       (let [for-business? (<sub [::business-entity? target-id])]
         (cc/button {:on-click #(>evt [::generate-letter target-id (:db/id @*template) for-business?])
                     :disabled (nil? @*template)}
                    (tr [:letter/generate]))))]))

(defn existing-letters [letters download-command]
  [:ul.mb-4
   (for-frag [letter (->> letters (remove (du/in-state-fn :letter.state/deleted)))]
     [:li.flex.h-6
      (if-let [file (-> letter :entity/_parent first)]
        [:span
         [file-download/download-file-link
          file
          {:command download-command
           :text    (-> letter :letter/template :letter-pdf-template/name)}]
         [:span.ml-2 ((<sub [::date-util/date-time-fn]) (-> file :entity/created))]]
        [:<>
         [:span.italic.mr-4
          (-> letter :letter/template :letter-pdf-template/name)]
         (when (du/in-state? :letter.state/generating letter)
           (cc/loading-spinner {:with-overlay false
                                :small        true}))])])])

(defn existing-letters-deletion-enabled [tr letters target-id download-command]
  [:table.w-full
   [:tbody
    (for-frag [letter letters
               :let [can-delete?        (<sub [::delete-letter-permitted? target-id letter])
                     letter-deleted?    (du/in-state? :letter.state/deleted letter)]
               :when (or can-delete? (not letter-deleted?))]
      (let [file              (-> letter :entity/_parent first)
            status-disabled   (or (nil? file) letter-deleted?)
            status-generating (du/in-state? :letter.state/generating letter)
            text              (or (-> letter :letter/template :letter-pdf-template/name) (:file/filename file))]
        [:tr
         {:class-name (if status-disabled "italic" "")}
         [:td
          (if (and file (not letter-deleted?))
            [file-download/download-file-link
             file
             {:command download-command
              :text    text}]
            [:span.leading-tight text])]
         (if status-generating
           (cc/loading-spinner {:with-overlay false
                                :small        true})
           [:td ((<sub [::date-util/date-time-fn]) (:entity/created file))])
         (when can-delete?
           [:td {:class-name "w-1/2"}
            (cond status-generating ""
                  (not letter-deleted?) (let [letter-id     (:db/id letter)
                                              for-business? (<sub [::business-entity? target-id])]
                                          [ui/modal-confirmation-button
                                           {:trigger-button-text (tr [:delete])
                                            :text-button?        true
                                            :tr                  tr
                                            :id                  (str "delete-letter-" text)
                                            :on-yes-click        #(>evt [::delete-letter
                                                                         target-id letter-id for-business?])}
                                           (tr [:letter/delete-confirm])])
                  :else (str "(" (tr [:letter/delete-success]) " "
                             ((<sub [::date-util/date-time-fn]) (-> letter :letter/deleted)) ")"))])]))]])

(defn letters-section
  "Show letters and controls for creating a new letter. `target-id` can be
  id of phase entity (pre-consultation, application, etc.) or licence."
  [{:keys [target-id tr]}]
  (let [generate-permitted? (<sub [::generate-letter-permitted? target-id])
        templates           (<sub [::letter-templates target-id])
        can-generate?       (and generate-permitted?
                                 (seq templates))
        letters             (<sub [::letters target-id])
        download-command    (if (<sub [::phase-entity? target-id])
                              :file/download-workspace-file
                              :file/download-letter-file)
        ui-improvements?    (<sub [::ff/enabled? :feature-flag.id/inspection-ui-improvements])
        form-open?          (<sub [::add-letter-form-open?])
        has-letters?        (seq letters)
        show-section?       (if ui-improvements?
                              (or has-letters? form-open?)
                              (or has-letters? can-generate?))]
    (cond
      show-section?
      [ws-section/section
       target-id
       :letters
       (util/mmap tr)
       (tr [:letter/additional-correspondence])
       false
       [:div
        (if (<sub [::ff/enabled? :feature-flag.id/delete-generic-letters])
          [existing-letters-deletion-enabled tr letters target-id download-command]
          [existing-letters letters download-command])
        (when generate-permitted?
          [create-letter-controls (util/mmap tr target-id templates)])]]
      (and generate-permitted? (<sub [::ws-subs/loading-config?]))
      [section/section-skeleton])))
